/* The one place the client talks to the server.
 *
 * Ported deliberately rather than rewritten. The old `api()` in
 * prototype/net.js carries two fixes that cost real debugging to find, and a
 * clean-room reimplementation would have reintroduced both:
 *
 *   1. A 401 from a sign-in attempt is not an expired session.
 *   2. A single 401 does not destroy a 30-day session on its own.
 *
 * Both are preserved below with the reasoning intact, because the reasoning is
 * the part that stops somebody "simplifying" them back out.
 */

import type { ApiError, ApiResponse } from '@shared/types';

export type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The response, plus the status. Callers narrow on `ok`. */
export type Result<T> = ApiResponse<T> & { status: number };

let token: string | null = null;

/* Session handling is a subscription rather than a direct call so that
   `api()` does not have to import React, and so a forced sign-out can reach
   the app from a background refetch as easily as from a click. */
type SignedOutHandler = () => void;
let onSignedOut: SignedOutHandler = () => {};
export const setSignedOutHandler = (fn: SignedOutHandler) => { onSignedOut = fn; };

const STORAGE_KEY = 'amon_token';

/* Storage that cannot take the page down with it.
 *
 * Safari in private mode, and any browser configured to block site data, throw
 * a SecurityError from localStorage rather than returning null. Reading it
 * anywhere inside a render — a useState initialiser, say — therefore throws
 * during render, and in React that is a blank page rather than a degraded one.
 *
 * Exported so nothing has to remember to wrap its own access. Every read
 * returns null on failure and every write is a no-op, which is exactly right:
 * these values are conveniences, and losing one costs a remembered tab, not a
 * session. */
export function readLocal(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}

export function writeLocal(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* private mode: the app works, the convenience does not */ }
}

export function getToken(): string | null {
  if (token) return token;
  token = readLocal(STORAGE_KEY);
  return token;
}

export function setToken(next: string | null): void {
  token = next;
  writeLocal(STORAGE_KEY, next);
}

/* Signing in is not a session expiring.
 *
 * A 401 from /api/auth/login means "those credentials are wrong". Treating it
 * as an expired session sent a second request to /api/auth/me — which of
 * course also 401s, because the person is on the sign-in screen and has no
 * session — and then threw, so the form's catch block ran and showed "cannot
 * reach the server" for a mistyped password. The pointless confirmation
 * request was also the mysterious second 401 in the console.
 *
 * You cannot be signed out of a session you were never in. These endpoints
 * hand the response back so the caller can say what actually happened. */
const PRE_AUTH =
  /^\/api\/(auth\/(login|signup|forgot|reset|verify|resend-verification)|signup-policy)/;

const NETWORK_ERROR: ApiError = {
  ok: false,
  error: 'network',
  hint: 'Could not reach the server. Check your connection and try again.'
};

export async function api<T = Record<string, unknown>>(
  method: Method,
  path: string,
  body?: unknown
): Promise<Result<T>> {
  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers: {
        'content-type': 'application/json',
        /* Marks this as the app rather than a stray fetch, so the server can
           answer an API path with JSON instead of the SPA's index.html. */
        'x-amon-client': '1',
        ...(getToken() ? { authorization: `Bearer ${getToken()}` } : {})
      },
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body)
    });
  } catch {
    /* The request never landed. This is the only case that is genuinely a
       connection problem, and it is the only case allowed to say so. */
    return { ...NETWORK_ERROR, status: 0 };
  }

  let json: unknown = null;
  try { json = await res.json(); } catch { /* 204s and HTML error pages */ }
  const payload = (json ?? {}) as Record<string, unknown>;

  if (res.status === 401 && !PRE_AUTH.test(path)) {
    /* A 401 is the server saying this session is not valid, so honour it — but
       only once, and only if the server still says so. A single 401 from a
       request that raced a token refresh should not destroy a 30-day session,
       so it is confirmed against /api/auth/me before anything is thrown away. */
    const stillBad = path === '/api/auth/me'
      ? true
      : await fetch('/api/auth/me', {
          headers: { ...(getToken() ? { authorization: `Bearer ${getToken()}` } : {}) },
          credentials: 'include'
        }).then(r => r.status === 401).catch(() => false);

    if (stillBad) {
      setToken(null);
      onSignedOut();
    }
    /* Either way the caller gets the response. A forced sign-out is a state
       change, not an exception — throwing here is what made a wrong password
       look like a dead server. */
  }

  return { ...(payload as object), status: res.status } as Result<T>;
}

export const get  = <T,>(path: string) => api<T>('GET', path);
export const post = <T,>(path: string, body?: unknown) => api<T>('POST', path, body);
export const put  = <T,>(path: string, body?: unknown) => api<T>('PUT', path, body);
export const patch = <T,>(path: string, body?: unknown) => api<T>('PATCH', path, body);
export const del  = <T,>(path: string) => api<T>('DELETE', path);

/** Every error code the server can actually return, said in a sentence.
 *
 *  Two rules, carried over from the old client. Every code the server can
 *  return is named here, so nobody is shown a bare `slow_down`. And anything
 *  unmapped still shows the code rather than "something went wrong", because a
 *  code can be searched for and a shrug cannot. */
const MESSAGES: Record<string, string> = {
  bad_credentials: 'That email and password do not match an account.',
  email_taken: 'That email already has an account. Sign in instead, or reset the password.',
  invalid_input: 'Check the email address, and use a password of at least 8 characters.',
  account_disabled: 'That account has been disabled. Get in touch if that is unexpected.',
  account_locked: 'Too many failed attempts. Try again in fifteen minutes.',
  too_many_attempts: 'Too many attempts from here. Wait a few minutes and try again.',
  too_many_signups: 'Too many accounts have been created from this address today.',
  slow_down: 'Too many requests from here. Wait a minute and try again.',
  too_many_requests: 'Too many requests from here. Wait a minute and try again.',
  challenge_expired: 'That took too long. Start signing in again.',
  bad_code: 'That code is not right. Codes change every 30 seconds — try the current one.',
  not_authenticated: 'You are signed out. Sign in again.',
  missing_token: 'That link is incomplete. Use the whole link from the email.',
  invalid_or_expired: 'That link has expired or has already been used. Request a new one.',
  email_not_verified: 'Confirm your email address to use the app.',
  network: 'Could not reach the server. Check your connection and try again.'
};

export function errorMessage(r: ApiError | null | undefined, fallback = 'That did not work.'): string {
  if (!r) return MESSAGES.network;
  /* The server's own sentence wins. It knows more about why than this table
     does, and it is written for the person rather than for the client. */
  if (r.hint) return r.hint;
  if (r.error && MESSAGES[r.error]) return MESSAGES[r.error];
  /* Unmapped: show the code. It is ugly, and it is searchable. */
  return r.error ? `${fallback} (${r.error})` : fallback;
}
